import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';

import { ApiError } from '@shared/api/client';

import { ResumableUploadError } from '../_shared/upload';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';
import { StateBadge } from '@shared/ui/StateBadge';
import { SurfaceError } from '@shared/ui/SurfaceError';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { formatNumber } from '@shared/format';

import { formatEpisodeCount, hasSeasons } from '../_shared/constants';
import { sharedCopy } from '../_shared/copy';
import { adminItemKey, toAcceptAttribute, useAdminContentItem, useUploadPolicy } from '../_shared/queries';
import type { Group, MediaAsset } from '../_shared/types';
import { RecordDialog } from './ContentAssets.RecordDialog';
import { SeriesPane } from './ContentAssets.SeriesPane';
import { ShapeCard } from './ContentAssets.ShapeCard';
import { ContentAssetsSkeleton } from './ContentAssets.Skeleton';
import { SingleAssetPane } from './ContentAssets.SingleAssetPane';
import { copy } from './copy';
import { errors } from './errors';
import {
  useCreateGroup,
  useDeleteDocument,
  useDeleteGroup,
  useDeleteMediaAsset,
  useReorderMediaAssets,
  useSetFreePreview,
  useUpdateGroup,
  useUpdateMediaAsset,
  useUploadDocument,
  useUploadMediaAsset,
} from './mutations';
import { joinAssetBytes, mediaAssetsKey, useMediaAssets } from './queries';

type DialogSubject =
  | { kind: 'group'; id: string; title: string; description: string | null }
  | { kind: 'asset'; id: string; title: string; description: string | null };

/**
 * `/content/:contentItemId/assets` — everything under the title.
 *
 * **The record's type decides the shape, and an operator never converts one into
 * the other from here.** A film is one asset and **zero seasons**, and no
 * synthetic «الموسم ١» is invented to make the two uniform: fabricating a parent
 * makes «is this a series?» unanswerable from the data and corrupts season
 * counting.
 *
 * **The trailer is its own section above both shapes.** A season is a partition
 * over the series' *ordered assets* and a trailer is not an episode, so it
 * belongs to no season and belongs to a film exactly as much as to a series. It
 * is free **by kind** — a badge tells, and no chip claims a control that could
 * never be switched off.
 *
 * **Every upload zone is scoped to what it fills.** One zone at the top of the
 * page leaves «where does this file land?» unanswerable in the markup and
 * unanswerable on screen; here the trailer has a zone, the film has a zone, and
 * each season has its own, each naming its destination in words. The drop *is*
 * the assignment — no second step assigns it, because any answer given
 * afterwards can be given wrongly.
 *
 * **Free marking is a chip per episode and a chip per season, never one radio
 * across the title.** `MAX_PREVIEW_ASSETS` is a range, so several assets may be
 * released without an entitlement and a single-select would forbid what the
 * setting permits. The season chip is a **shortcut over its episodes** rather
 * than a third entitled thing — the entitlement lands on the asset, so the chip
 * writes every episode's flag and reads its own state back from them.
 */
export function ContentAssetsPage() {
  const { contentItemId } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();

  const itemQuery = useAdminContentItem(contentItemId);
  const assetsQuery = useMediaAssets(contentItemId);
  const uploadPolicy = useUploadPolicy();

  const createGroup = useCreateGroup();
  const updateGroup = useUpdateGroup();
  const deleteGroup = useDeleteGroup();
  const updateAsset = useUpdateMediaAsset();
  const deleteAsset = useDeleteMediaAsset();
  const reorderAssets = useReorderMediaAssets();
  const setFreePreview = useSetFreePreview();
  const uploadAsset = useUploadMediaAsset();
  const uploadDocument = useUploadDocument();
  const deleteDocument = useDeleteDocument();

  const [dialogSubject, setDialogSubject] = useState<DialogSubject | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [confirmSubject, setConfirmSubject] = useState<ConfirmSubject | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<(() => void) | null>(null);
  // Keyed by destination — `TRAILER`, `FEATURE`, or a season's id — so several
  // uploads run at once and each reports on the zone that started it.
  const [uploadProgress, setUploadProgress] = useState<Record<string, number | null>>({});
  const [uploadErrors, setUploadErrors] = useState<Record<string, string | null>>({});

  const item = itemQuery.data;
  // `content` decides which assets exist and in what order; `assets` holds the
  // free marking and the two states and no ordinal at all. The row the surface
  // draws is the join of the two, and neither read alone can draw it.
  const assets = joinAssetBytes(item?.media_assets ?? [], assetsQuery.data ?? []);
  const groups: Group[] = item?.groups ?? [];

  const series = item ? hasSeasons(item.content_type) : false;
  const trailer = assets.find((asset) => asset.kind === 'TRAILER') ?? null;
  const feature = assets.find((asset) => asset.kind === 'FEATURE') ?? null;
  const episodes = assets.filter((asset) => asset.kind === 'EPISODE');

  function failureMessage(error: unknown) {
    // The resumable transport is not the enveloped client, so its refusals are
    // not `ApiError`s — but they carry the same catalogued code, and reading it
    // is what keeps «this format is not accepted» from arriving as «something
    // unexpected happened» on the one surface where the operator can act on it.
    const status = error instanceof ApiError || error instanceof ResumableUploadError ? error.status : 0;
    const code =
      error instanceof ApiError
        ? error.code
        : error instanceof ResumableUploadError
          ? (error.code ?? undefined)
          : undefined;

    // A timeout on a removal is an **unknown**, not a failure: the copy directs
    // to a reload rather than offering a retry that might delete twice.
    if (status === 504) return copy.toast.unknownOutcome;

    return resolveErrorCopy(code, status, errors).message;
  }

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: mediaAssetsKey(contentItemId ?? '') });
    void queryClient.invalidateQueries({ queryKey: adminItemKey(contentItemId ?? '') });
  }

  /**
   * The two acts every asset row offers whatever section it sits in — open its
   * editor, attach a document to it. Named here rather than inlined per section
   * so the trailer, the film file, and every episode reach the same code: the
   * editor dialog is one element serving the whole page, and a subtitle track
   * attaches to a `MEDIA_ASSET` identically wherever the row was drawn.
   */
  function openAssetEditor(asset: MediaAsset) {
    setDialogSubject({
      kind: 'asset',
      id: asset.id,
      title: asset.title,
      description: asset.description,
    });
  }

  function addAssetDocument(asset: MediaAsset, file: File) {
    uploadDocument.mutate(
      { subjectType: 'MEDIA_ASSET', subjectId: asset.id, file },
      {
        onSuccess: () => toast.show(copy.toast.documentUploaded, 'success'),
        onError: (error) => toast.show(failureMessage(error), 'danger'),
      },
    );
  }

  /** One dialog serves every removal, and it names what goes with the thing. */
  function askRemoval(subject: ConfirmSubject, run: () => void) {
    setConfirmSubject(subject);
    setPendingRemoval(() => run);
  }

  function closeConfirm() {
    setConfirmSubject(null);
    setPendingRemoval(null);
  }

  function askRemoveAsset(asset: MediaAsset) {
    // The one refusal on this surface, drawn as a refusal rather than as a
    // missing button: the control stays and the dialog opens with no danger
    // button and the way out in its body.
    const isOnlyPlayable = assets.filter((row) => row.kind !== 'TRAILER').length <= 1;
    if (asset.kind !== 'TRAILER' && isOnlyPlayable && item?.state === 'PUBLISHED') {
      setConfirmSubject({
        heading: copy.remove.refusal.heading,
        body: copy.remove.refusal.body,
        commitLabel: copy.remove.refusal.commit,
        intent: 'warning',
        refusal: true,
      });
      setPendingRemoval(null);
      return;
    }

    const subject: ConfirmSubject =
      asset.kind === 'TRAILER'
        ? {
            heading: copy.remove.trailer.heading,
            body: copy.remove.trailer.body,
            commitLabel: copy.remove.trailer.commit,
            intent: 'danger',
          }
        : {
            heading: copy.remove.asset.heading(asset.title),
            body: copy.remove.asset.body(asset.title),
            commitLabel: copy.remove.asset.commit,
            intent: 'danger',
          };

    askRemoval(subject, () =>
      deleteAsset.mutate(
        { contentItemId: contentItemId ?? '', mediaAssetId: asset.id },
        {
          onSuccess: () => {
            closeConfirm();
            toast.show(copy.toast.removed, 'success');
          },
          onError: (error) => {
            closeConfirm();
            toast.show(failureMessage(error), 'danger');
          },
        },
      ),
    );
  }

  function askRemoveGroup(group: Group, episodeCount: number) {
    askRemoval(
      {
        heading: copy.remove.group.heading(group.title),
        // The count is what makes the sentence worth reading before the press.
        body: copy.remove.group.body(group.title, formatEpisodeCount(episodeCount)),
        commitLabel: copy.remove.group.commit,
        intent: 'danger',
      },
      () =>
        deleteGroup.mutate(
          { contentItemId: contentItemId ?? '', groupId: group.id },
          {
            onSuccess: () => {
              closeConfirm();
              toast.show(copy.toast.removed, 'success');
            },
            onError: (error) => {
              closeConfirm();
              toast.show(failureMessage(error), 'danger');
            },
          },
        ),
    );
  }

  function askRemoveDocument(documentId: string, label: string, subjectId: string, owner: string) {
    askRemoval(
      {
        heading: copy.remove.document.heading,
        body: copy.remove.document.body(label, owner),
        commitLabel: copy.remove.document.commit,
        intent: 'danger',
      },
      () =>
        deleteDocument.mutate(
          { documentId, subjectType: 'MEDIA_ASSET', subjectId },
          {
            onSuccess: () => {
              closeConfirm();
              toast.show(copy.toast.removed, 'success');
            },
            onError: (error) => {
              closeConfirm();
              toast.show(failureMessage(error), 'danger');
            },
          },
        ),
    );
  }

  /**
   * Start one upload, and report it on the zone it was dropped into.
   *
   * The zone key is the destination — `TRAILER`, `FEATURE`, or the season's id —
   * which is what the drop already decided. Keying by it is what lets a trailer,
   * a film file, and two seasons upload at once and each say how far it has got;
   * one «uploading» flag would be four uploads sharing a single sentence.
   *
   * **A rejected file fails its own zone.** The refusal is drawn under that zone
   * and nothing else on the page is disturbed: the other uploads keep going, and
   * whatever is already stored stays stored.
   */
  function upload(kind: string, groupId: string | null, files: FileList) {
    const file = files[0];
    if (!file) return;

    const zone = groupId ?? kind;
    const chunkSizeBytes = uploadPolicy.data?.resumable_upload?.chunk_size_bytes;
    if (!chunkSizeBytes) {
      setZoneError(zone, copy.upload.policyUnavailable);
      return;
    }

    const refusal = localRefusal(file);
    if (refusal) {
      setZoneError(zone, refusal);
      return;
    }

    setZoneError(zone, null);
    setZoneProgress(zone, 0);

    uploadAsset.mutate(
      {
        contentItemId: contentItemId ?? '',
        kind,
        groupId,
        file,
        chunkSizeBytes,
        onProgress: (fraction) => setZoneProgress(zone, fraction),
      },
      {
        onSuccess: () => {
          setZoneProgress(zone, null);
          toast.show(copy.toast.uploaded, 'success');
        },
        onError: (error) => {
          setZoneProgress(zone, null);
          setZoneError(zone, failureMessage(error));
        },
      },
    );
  }

  /**
   * The two refusals worth making before a byte moves.
   *
   * Both are re-checked by the service and neither is trusted from here. This is
   * about not spending twenty minutes of an operator's connection to be told
   * something the browser already knew. The values are the published ones, so a
   * cap change reaches this check without a release.
   */
  function localRefusal(file: File): string | null {
    const accepted = uploadPolicy.data?.accepted_video_formats ?? [];
    if (accepted.length > 0 && !accepted.includes(file.type)) {
      return copy.upload.unsupportedFormat(accepted.join(' · '));
    }

    const cap = uploadPolicy.data?.max_video_size_bytes;
    if (cap !== null && cap !== undefined && file.size > cap) {
      return copy.upload.tooLarge(formatNumber(Math.floor(cap / (1024 * 1024))));
    }

    return null;
  }

  function setZoneProgress(zone: string, fraction: number | null) {
    setUploadProgress((current) => ({ ...current, [zone]: fraction }));
  }

  function setZoneError(zone: string, message: string | null) {
    setUploadErrors((current) => ({ ...current, [zone]: message }));
  }

  function moveAsset(list: MediaAsset[], asset: MediaAsset, direction: 'up' | 'down') {
    const index = list.findIndex((row) => row.id === asset.id);
    const target = direction === 'up' ? index - 1 : index + 1;
    // The end of a list is announced by not moving: pressing «up» on the first
    // row moves nothing, and the toast says so rather than failing silently.
    if (target < 0 || target >= list.length) return;

    const ordered = [...list];
    const moved = ordered[index];
    const swapped = ordered[target];
    if (!moved || !swapped) return;
    ordered[index] = swapped;
    ordered[target] = moved;

    reorderAssets.mutate(
      { contentItemId: contentItemId ?? '', orderedIds: ordered.map((row) => row.id) },
      {
        onSuccess: () => toast.show(copy.toast.reordered, 'success'),
        onError: (error) => toast.show(failureMessage(error), 'danger'),
      },
    );
  }

  if (itemQuery.isError || assetsQuery.isError) {
    return (
      <>
        <Breadcrumb
          items={[
            { label: sharedCopy.crumbs.console, to: '/' },
            { label: sharedCopy.crumbs.content, to: '/content' },
          ]}
        />
        <div className="mt-6">
          <SurfaceError error={itemQuery.error ?? assetsQuery.error} catalog={errors} onRetry={invalidate} />
        </div>
      </>
    );
  }

  if (!item) {
    return (
      <div className="mt-6">
        <ContentAssetsSkeleton />
      </div>
    );
  }

  const videoAccept = toAcceptAttribute(uploadPolicy.data?.accepted_video_formats);

  return (
    <>
      {/*
        Four levels: the third names the title and points at its **edit** route,
        because `/content/:id` is not a route and a crumb that led nowhere would
        be worse than a deep trail.
      */}
      <Breadcrumb
        items={[
          { label: sharedCopy.crumbs.console, to: '/' },
          { label: sharedCopy.crumbs.content, to: '/content' },
          { label: item.title, to: `/content/${item.id}/edit` },
          { label: copy.crumb },
        ]}
      />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">
          {series ? copy.head.ledeSeries : copy.head.ledeFilm}
        </p>
      </div>

      <ShapeCard
        series={series}
        contentType={item.content_type}
        seasonCount={groups.length}
        assetCount={series ? episodes.length : feature ? 1 : 0}
        uploadPolicy={uploadPolicy.data}
        onAddTitleDocument={(file) =>
          uploadDocument.mutate(
            { subjectType: 'CONTENT_ITEM', subjectId: item.id, file },
            {
              onSuccess: () => toast.show(copy.toast.documentUploaded, 'success'),
              onError: (error) => toast.show(failureMessage(error), 'danger'),
            },
          )
        }
      />

      <SingleAssetPane
        sectionId="assets-trailer"
        heading={copy.trailer.heading}
        badge={<StateBadge kind="success">{copy.trailer.alwaysFree}</StateBadge>}
        lede={copy.trailer.lede}
        asset={trailer}
        emptyMessage={copy.trailer.empty}
        freeControl="always-free"
        uploadPolicy={uploadPolicy.data}
        busyDocument={uploadDocument.isPending}
        accept={videoAccept}
        dropLabel={copy.trailer.dropLabel}
        dropHint={copy.trailer.dropHint((uploadPolicy.data?.accepted_video_formats ?? []).join(' · '))}
        uploadProgress={uploadProgress.TRAILER ?? null}
        uploadError={uploadErrors.TRAILER ?? null}
        onFiles={(files) => upload('TRAILER', null, files)}
        onEdit={openAssetEditor}
        onRemove={askRemoveAsset}
        onAddDocument={addAssetDocument}
        onRemoveDocument={(asset, documentId, label) =>
          askRemoveDocument(documentId, label, asset.id, asset.title)
        }
      />

      {series ? (
        <SeriesPane
          groups={groups}
          episodes={episodes}
          uploadPolicy={uploadPolicy.data}
          videoAccept={videoAccept}
          busyDocument={uploadDocument.isPending}
          uploadProgress={uploadProgress}
          uploadErrors={uploadErrors}
          onAddSeason={() =>
            createGroup.mutate(
              { contentItemId: item.id, title: copy.seasons.newTitle },
              {
                onSuccess: (group) => {
                  toast.show(copy.seasons.added, 'success');
                  // An action whose result is invisible reads as a broken
                  // control, so the new season opens its own editor at once.
                  setDialogSubject({
                    kind: 'group',
                    id: group.id,
                    title: group.title,
                    description: group.description,
                  });
                },
                onError: (error) => toast.show(failureMessage(error), 'danger'),
              },
            )
          }
          onEditGroup={(group) =>
            setDialogSubject({
              kind: 'group',
              id: group.id,
              title: group.title,
              description: group.description,
            })
          }
          onRemoveGroup={askRemoveGroup}
          onUploadEpisode={(groupId, files) => upload('EPISODE', groupId, files)}
          onEditAsset={(asset) =>
            setDialogSubject({
              kind: 'asset',
              id: asset.id,
              title: asset.title,
              description: asset.description,
            })
          }
          onRemoveAsset={askRemoveAsset}
          onMoveAsset={(list, asset, direction) => moveAsset(list, asset, direction)}
          onToggleFree={(asset) => {
            const free = !asset.is_free_preview;

            setFreePreview.mutate(
              {
                contentItemId: item.id,
                mediaAssetId: asset.id,
                free,
              },
              {
                // **A success that says so.** Every other action on this page
                // toasts and this one did not, so pressing the chip and having
                // it work was indistinguishable from pressing it and having
                // nothing happen — the refusal at the preview ceiling spoke and
                // the acceptance did not.
                onSuccess: () =>
                  toast.show(free ? copy.toast.markedFree : copy.toast.unmarkedFree, 'success'),
                onError: (error) => toast.show(failureMessage(error), 'danger'),
              },
            );
          }}
          onAddDocument={(subjectId, file) =>
            uploadDocument.mutate(
              { subjectType: 'MEDIA_ASSET', subjectId, file },
              {
                onSuccess: () => toast.show(copy.toast.documentUploaded, 'success'),
                onError: (error) => toast.show(failureMessage(error), 'danger'),
              },
            )
          }
          onAddSeasonDocument={(groupId, file) =>
            uploadDocument.mutate(
              { subjectType: 'GROUP', subjectId: groupId, file },
              {
                onSuccess: () => toast.show(copy.toast.documentUploaded, 'success'),
                onError: (error) => toast.show(failureMessage(error), 'danger'),
              },
            )
          }
          onRemoveDocument={askRemoveDocument}
        />
      ) : (
        <SingleAssetPane
          sectionId="assets-film"
          heading={copy.film.heading}
          lede={copy.film.lede}
          asset={feature}
          emptyMessage={copy.film.empty}
          freeControl="none"
          uploadPolicy={uploadPolicy.data}
          busyDocument={uploadDocument.isPending}
          accept={videoAccept}
          dropLabel={copy.film.dropLabel}
          dropHint={copy.film.dropHint}
          uploadProgress={uploadProgress.FEATURE ?? null}
          uploadError={uploadErrors.FEATURE ?? null}
          onFiles={(files) => upload('FEATURE', null, files)}
          onEdit={openAssetEditor}
          onRemove={askRemoveAsset}
          onAddDocument={addAssetDocument}
          onRemoveDocument={(asset, documentId, label) =>
            askRemoveDocument(documentId, label, asset.id, asset.title)
          }
        />
      )}

      {/* Said once at the end rather than repeated on every rung. */}
      <p className="mb-0 max-w-measure text-xs text-ink-muted">{copy.streamOnly}</p>

      <RecordDialog
        open={dialogSubject !== null}
        kind={dialogSubject?.kind ?? 'asset'}
        subjectTitle={dialogSubject?.title ?? ''}
        subjectDescription={dialogSubject?.description ?? null}
        busy={updateGroup.isPending || updateAsset.isPending}
        formError={dialogError}
        onCancel={() => {
          setDialogSubject(null);
          setDialogError(null);
        }}
        onSave={(values) => {
          const subject = dialogSubject;
          if (!subject) return;
          setDialogError(null);

          const handlers = {
            onSuccess: () => setDialogSubject(null),
            onError: (error: unknown) => setDialogError(failureMessage(error)),
          };

          if (subject.kind === 'group') {
            updateGroup.mutate({ contentItemId: item.id, groupId: subject.id, body: values }, handlers);
            return;
          }

          updateAsset.mutate({ contentItemId: item.id, mediaAssetId: subject.id, body: values }, handlers);
        }}
      />

      <ConfirmDialog
        open={confirmSubject !== null}
        subject={confirmSubject}
        busy={deleteAsset.isPending || deleteGroup.isPending || deleteDocument.isPending}
        onConfirm={() => pendingRemoval?.()}
        onCancel={closeConfirm}
      />
    </>
  );
}
